/**
 * Member Billing Info Form Component
 *
 * Form for editing member's billing and professional information.
 */

import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { ServiceFacilityResponse2 } from '@store/generated/billing';
import type { BillingEntityResponse } from '@store/generated/billing-entities';
import type { ProviderLicenseType } from '@store/generated/organizations';
import { MuiTelInput } from 'mui-tel-input';

// License type options with display labels
const LICENSE_TYPE_OPTIONS: Array<{ value: ProviderLicenseType; label: string }> = [
  { value: 'MD', label: 'MD - Doctor of Medicine' },
  { value: 'DO', label: 'DO - Doctor of Osteopathic Medicine' },
  { value: 'PhD', label: 'PhD - Doctor of Philosophy (Psychology)' },
  { value: 'PsyD', label: 'PsyD - Doctor of Psychology' },
  { value: 'LCSW', label: 'LCSW - Licensed Clinical Social Worker' },
  { value: 'LMSW', label: 'LMSW - Licensed Master Social Worker' },
  { value: 'LISW', label: 'LISW - Licensed Independent Social Worker' },
  { value: 'LICSW', label: 'LICSW - Licensed Independent Clinical Social Worker' },
  { value: 'LCSW-C', label: 'LCSW-C - Licensed Certified Social Worker - Clinical' },
  { value: 'LMFT', label: 'LMFT - Licensed Marriage and Family Therapist' },
  { value: 'LPC', label: 'LPC - Licensed Professional Counselor' },
  { value: 'LCPC', label: 'LCPC - Licensed Clinical Professional Counselor' },
  { value: 'LPCC', label: 'LPCC - Licensed Professional Clinical Counselor' },
  { value: 'LMHC', label: 'LMHC - Licensed Mental Health Counselor' },
  { value: 'LCMHC', label: 'LCMHC - Licensed Clinical Mental Health Counselor' },
  { value: 'NP', label: 'NP - Nurse Practitioner' },
  { value: 'PMHNP', label: 'PMHNP - Psychiatric Mental Health Nurse Practitioner' },
  { value: 'APRN', label: 'APRN - Advanced Practice Registered Nurse' },
  { value: 'CNS', label: 'CNS - Clinical Nurse Specialist' },
  { value: 'PA', label: 'PA - Physician Assistant' },
  { value: 'RN', label: 'RN - Registered Nurse' },
  { value: 'LADC', label: 'LADC - Licensed Alcohol and Drug Counselor' },
  { value: 'LCADC', label: 'LCADC - Licensed Clinical Alcohol and Drug Counselor' },
  { value: 'CADC', label: 'CADC - Certified Alcohol and Drug Counselor' },
  { value: 'BCBA', label: 'BCBA - Board Certified Behavior Analyst' },
  { value: 'PSYCH_ASSOC', label: 'Psychology Associate' },
  { value: 'PASTORAL', label: 'Pastoral Counselor' },
  { value: 'OTHER', label: 'Other' },
];

interface BillingInfoFormData {
  billing_entity_id?: string | null;
  service_facility_id?: string | null;
  taxonomy_code?: string | null;
  license_type?: ProviderLicenseType | null;
  license_number?: string | null;
  license_state?: string | null;
  npi_number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  communications_name?: string | null;
  signing_name?: string | null;
  facility_address?: string | null;
  facility_address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  phone?: string | null;
  fax_number?: string | null;
  tax_id?: string | null;
  tax_id_type?: string | null;
  place_of_service?: string | null;
}

interface MemberBillingInfoFormProps {
  data: BillingInfoFormData;
  onChange: (field: keyof BillingInfoFormData, value: string) => void;
  disabled?: boolean;
  billingEntities?: BillingEntityResponse[];
  entitiesLoading?: boolean;
  serviceFacilities?: ServiceFacilityResponse2[];
  facilitiesLoading?: boolean;
}

export function MemberBillingInfoForm({
  data,
  onChange,
  disabled = false,
  billingEntities = [],
  entitiesLoading = false,
  serviceFacilities = [],
  facilitiesLoading = false,
}: MemberBillingInfoFormProps) {
  // Get the default entity/facility for display
  const defaultEntity = billingEntities.find(e => e.is_default);
  const defaultFacility = serviceFacilities.find(f => f.is_default);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Billing Identity Selection */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Billing Identity
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel>Billing Identity</InputLabel>
          <Select
            value={data.billing_entity_id || ''}
            onChange={(e) => onChange('billing_entity_id', e.target.value || '')}
            disabled={disabled || entitiesLoading}
            label="Billing Identity"
            startAdornment={entitiesLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          >
            <MenuItem value="">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <em>Use organization default</em>
                {defaultEntity && (
                  <Typography variant="caption" color="text.secondary">
                    ({defaultEntity.legal_name})
                  </Typography>
                )}
              </Box>
            </MenuItem>
            {billingEntities.map((entity) => (
              <MenuItem key={entity.billing_entity_id} value={entity.billing_entity_id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <span>{entity.legal_name}</span>
                  <Typography variant="caption" color="text.secondary">
                    (NPI: {entity.billing_npi})
                  </Typography>
                  {entity.is_default && (
                    <Chip
                      label="Default"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 20, ml: 'auto' }}
                    />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Override which billing identity is used for this clinician&apos;s claims. Leave blank to use your organization&apos;s default.
          </Typography>
        </FormControl>
      </Box>

      {/* Service Facility Selection */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Service Facility
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel>Service Facility</InputLabel>
          <Select
            value={data.service_facility_id || ''}
            onChange={(e) => onChange('service_facility_id', e.target.value || '')}
            disabled={disabled || facilitiesLoading}
            label="Service Facility"
            startAdornment={facilitiesLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          >
            <MenuItem value="">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <em>Use organization default</em>
                {defaultFacility && (
                  <Typography variant="caption" color="text.secondary">
                    ({defaultFacility.display_name})
                  </Typography>
                )}
              </Box>
            </MenuItem>
            {serviceFacilities.map((facility) => (
              <MenuItem key={facility.service_facility_id} value={facility.service_facility_id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <span>{facility.display_name}</span>
                  {facility.address?.city && (
                    <Typography variant="caption" color="text.secondary">
                      ({facility.address.city}, {facility.address.state})
                    </Typography>
                  )}
                  {facility.is_default && (
                    <Chip
                      label="Default"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 20, ml: 'auto' }}
                    />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Override which service facility (Box 32) is used for this clinician&apos;s claims. Leave blank to use your organization&apos;s default.
          </Typography>
        </FormControl>
      </Box>

      <Divider />

      {/* Professional Information */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Professional Information
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>License Type</InputLabel>
            <Select
              value={data.license_type || ''}
              onChange={(e) => onChange('license_type', e.target.value)}
              disabled={disabled}
              label="License Type"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {LICENSE_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="License Number"
              value={data.license_number || ''}
              onChange={(e) => onChange('license_number', e.target.value)}
              disabled={disabled}
              fullWidth
              size="small"
            />
            <TextField
              label="License State"
              value={data.license_state || ''}
              onChange={(e) => onChange('license_state', e.target.value)}
              disabled={disabled}
              fullWidth
              size="small"
              placeholder="CA"
            />
          </Box>

          <TextField
            label="NPI Number"
            value={data.npi_number || ''}
            onChange={(e) => onChange('npi_number', e.target.value)}
            disabled={disabled}
            fullWidth
            size="small"
          />

          <TextField
            label="Taxonomy Code"
            value={data.taxonomy_code || ''}
            onChange={(e) => onChange('taxonomy_code', e.target.value)}
            disabled={disabled}
            fullWidth
            size="small"
            placeholder="e.g., 2084P0800X"
            helperText="Rendering provider taxonomy code (10-char alphanumeric ending in X)"
            inputProps={{ maxLength: 10 }}
          />
        </Box>
      </Box>

      <Divider />

      {/* Names for Billing */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Names for Billing
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First Name (Billing)"
              value={data.first_name || ''}
              onChange={(e) => onChange('first_name', e.target.value)}
              disabled={disabled}
              fullWidth
              size="small"
            />
            <TextField
              label="Last Name (Billing)"
              value={data.last_name || ''}
              onChange={(e) => onChange('last_name', e.target.value)}
              disabled={disabled}
              fullWidth
              size="small"
            />
          </Box>

          <TextField
            label="Communications Name"
            value={data.communications_name || ''}
            onChange={(e) => onChange('communications_name', e.target.value)}
            disabled={disabled}
            fullWidth
            size="small"
            helperText="Name used in patient communications"
          />

          <TextField
            label="Signing Name"
            value={data.signing_name || ''}
            onChange={(e) => onChange('signing_name', e.target.value)}
            disabled={disabled}
            fullWidth
            size="small"
            helperText="Name used for signing documents (e.g., 'John Doe, LCSW')"
          />
        </Box>
      </Box>

      <Divider />

      {/* Address */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Facility Address
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Address Line 1"
            value={data.facility_address || ''}
            onChange={(e) => onChange('facility_address', e.target.value)}
            disabled={disabled}
            fullWidth
            size="small"
          />

          <TextField
            label="Address Line 2"
            value={data.facility_address2 || ''}
            onChange={(e) => onChange('facility_address2', e.target.value)}
            disabled={disabled}
            fullWidth
            size="small"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="City"
              value={data.city || ''}
              onChange={(e) => onChange('city', e.target.value)}
              disabled={disabled}
              fullWidth
              size="small"
            />
            <TextField
              label="State"
              value={data.state || ''}
              onChange={(e) => onChange('state', e.target.value)}
              disabled={disabled}
              size="small"
              sx={{ width: 120 }}
              placeholder="CA"
            />
            <TextField
              label="ZIP Code"
              value={data.zip || ''}
              onChange={(e) => onChange('zip', e.target.value)}
              disabled={disabled}
              size="small"
              sx={{ width: 150 }}
            />
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* Contact & Tax Information */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Contact & Tax Information
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <MuiTelInput
              label="Phone Number"
              value={data.phone || ''}
              onChange={(value) => onChange('phone', value)}
              disabled={disabled}
              fullWidth
              size="small"
              defaultCountry="US"
              forceCallingCode
            />
            <MuiTelInput
              label="Fax Number"
              value={data.fax_number || ''}
              onChange={(value) => onChange('fax_number', value)}
              disabled={disabled}
              fullWidth
              size="small"
              defaultCountry="US"
              forceCallingCode
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Tax ID"
              value={data.tax_id || ''}
              onChange={(e) => onChange('tax_id', e.target.value)}
              disabled={disabled}
              fullWidth
              size="small"
              helperText="EIN or SSN (masked)"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Tax ID Type</InputLabel>
              <Select
                value={data.tax_id_type || ''}
                onChange={(e) => onChange('tax_id_type', e.target.value)}
                disabled={disabled}
                label="Tax ID Type"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value="EIN">EIN</MenuItem>
                <MenuItem value="SSN">SSN</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TextField
            label="Place of Service Code"
            value={data.place_of_service || ''}
            onChange={(e) => onChange('place_of_service', e.target.value)}
            disabled={disabled}
            fullWidth
            size="small"
            helperText="Standard place of service code (e.g., '11' for office)"
          />
        </Box>
      </Box>
    </Box>
  );
}
